import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isDoaga } from "../lib/chf";
import { CalendarPane } from "./PersonDetail";
import { Input } from "../components/ui/input";
import { AuthImg } from "../components/AuthImg";
import { toast } from "sonner";
import { Search, Users, ArrowLeft } from "lucide-react";
import ProjectHoursView from "./hours/ProjectHoursView";
import ActivityFeed from "../components/ActivityFeed";

const fullName = (p) => `${p.nume || ""} ${p.prenume || ""}`.trim() || "(fără nume)";

export default function HoursOverview() {
  const navigate = useNavigate();
  const { canDo, isAdmin, user } = useAuth();
  const canEdit = canDo("PERSONAL", "edit");
  const doagaBlocked = isDoaga(user);

  const [persons, setPersons] = useState([]);
  const [employerCompanies, setEmployerCompanies] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [salaryMonth, setSalaryMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("");
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("convic_persons_sort") || "convic_first");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("convic_hours_view") || "persons"); // "persons" | "projects"

  useEffect(() => {
    localStorage.setItem("convic_hours_view", viewMode);
  }, [viewMode]);

  useEffect(() => {
    Promise.all([
      api.get("/persons"),
      api.get("/employer-companies").catch(() => ({ data: [] })),
      api.get(`/salary-payments?month=${salaryMonth}`).catch(() => ({ data: [] })),
    ]).then(([p, ec, sp]) => {
      const list = (p.data || []).filter((x) => x.active !== false);
      setPersons(list);
      setEmployerCompanies(ec.data || []);
      setSalaryPayments(sp.data || []);
      // Auto-select first matching person
      if (list.length > 0) {
        // For non-admin, jump straight to own person
        if (!isAdmin && user?.person_id && list.find((x) => x.id === user.person_id)) {
          setSelectedId(user.person_id);
        } else {
          setSelectedId(list[0].id);
        }
      }
    });
  }, [isAdmin, user?.person_id, salaryMonth]);

  // Cycle status pe click bulină: unpaid → partial → paid → unpaid
  const cyclePaymentStatus = async (personId, currentStatus, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!canEdit && !isAdmin) {
      toast.error("Nu ai permisiune să modifici statusul plății");
      return;
    }
    const order = ["unpaid", "partial", "paid"];
    const idx = order.indexOf(currentStatus || "unpaid");
    const next = order[(idx + 1) % order.length];
    // Optimistic update
    setSalaryPayments((prev) => {
      const existing = prev.find((s) => s.person_id === personId);
      if (existing) return prev.map((s) => s.person_id === personId ? { ...s, status: next } : s);
      return [...prev, { person_id: personId, month: salaryMonth, status: next }];
    });
    try {
      await api.put(`/salary-payments/${personId}/${salaryMonth}`, { status: next });
      const label = next === "paid" ? "plătit" : next === "partial" ? "parțial" : "neplătit";
      toast.success(`Status actualizat: ${label}`);
    } catch (err) {
      // Revert pe eroare
      const d = err?.response?.data;
      toast.error((d && (d.detail || d.message)) || "Nu s-a putut actualiza statusul");
      setSalaryPayments((prev) => prev.map((s) => s.person_id === personId ? { ...s, status: currentStatus } : s));
    }
  };

  const sorted = useMemo(() => {    const list = persons.filter((p) =>
      !filter || `${p.nume || ""} ${p.prenume || ""}`.toLowerCase().includes(filter.toLowerCase())
    );
    const firmaName = (p) => (employerCompanies.find((c) => c.id === p.firma_id)?.name || "").toLowerCase();
    const isConVic = (p) => firmaName(p).includes("convic");
    list.sort((a, b) => {
      if (sortMode === "name_asc") return fullName(a).localeCompare(fullName(b), "ro");
      // convic_first default
      const ca = isConVic(a) ? 0 : 1;
      const cb = isConVic(b) ? 0 : 1;
      if (ca !== cb) return ca - cb;
      return fullName(a).localeCompare(fullName(b), "ro");
    });
    return list;
  }, [persons, filter, sortMode, employerCompanies]);

  const selected = persons.find((p) => p.id === selectedId);
  const defaultRate = parseFloat(selected?.hourly_rate || 0) || 0;

  if (doagaBlocked) return <div className="text-sm text-zinc-500 p-6" data-testid="restricted-msg">Nu ai acces la această secțiune.</div>;
  return (
    <div data-testid="page-hours-overview">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 text-sm">
            <ArrowLeft size={14}/> Înapoi
          </button>
          <div>
            <h1 className="font-display text-3xl font-extrabold">
              Calendar Ore — {viewMode === "projects" ? "Proiecte" : "Personal"}
            </h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              {viewMode === "projects"
                ? "Click pe un proiect/regie pentru a completa orele tuturor persoanelor alocate într-o singură vedere."
                : `${persons.length} persoane active. Click pe un nume pentru a-i deschide calendarul.`}
            </p>
          </div>
        </div>

        {/* Toggle Persoane / Proiecte (vizibil pentru admin + cei cu PERSONAL:edit) */}
        {(isAdmin || canEdit) && (
          <div className="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-0.5" data-testid="hours-view-toggle">
            <button
              type="button"
              onClick={() => setViewMode("persons")}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                viewMode === "persons" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"
              }`}
              data-testid="hours-view-persons"
            >
              👥 Persoane
            </button>
            <button
              type="button"
              onClick={() => setViewMode("projects")}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                viewMode === "projects" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"
              }`}
              data-testid="hours-view-projects"
            >
              🏗 Proiecte
            </button>
          </div>
        )}
      </div>

      {viewMode === "projects" ? (
        <ProjectHoursView canEdit={canEdit || isAdmin} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* LEFT: persons list */}
        <div className="card-flat p-3 max-h-[calc(100vh-220px)] flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <Input
              type="month"
              value={salaryMonth}
              onChange={(e) => setSalaryMonth(e.target.value)}
              className="h-7 text-[11px] w-[130px]"
              data-testid="salary-month-select"
            />
            <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 ring-1 ring-white"/>plătit</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 ring-1 ring-white"/>parțial</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 ring-1 ring-white"/>neplătit</span>
            </div>
          </div>
          <div className="relative mb-2">
            <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-400"/>
            <Input
              className="pl-8"
              placeholder="Caută persoană..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="hours-search"
            />
          </div>
          <div className="flex-1 overflow-y-auto space-y-1" data-testid="persons-list">
            {sorted.length === 0 ? (
              <div className="text-sm text-zinc-400 italic p-3 text-center">Niciun rezultat.</div>
            ) : (
              sorted.map((p) => {
                const isSelected = p.id === selectedId;
                const fname = (employerCompanies.find((c) => c.id === p.firma_id)?.name || "—");
                const pay = salaryPayments.find((s) => s.person_id === p.id);
                const status = pay?.status || "unpaid";
                const dotColor = status === "paid" ? "bg-green-500" : status === "partial" ? "bg-amber-400" : "bg-red-500";
                const dotTitle = status === "paid"
                  ? `Salariu ${salaryMonth} — plătit`
                  : status === "partial"
                  ? `Salariu ${salaryMonth} — plătit parțial`
                  : `Salariu ${salaryMonth} — neplătit`;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedId(p.id)}
                    className={`w-full text-left flex items-center gap-2.5 px-2.5 py-1.5 rounded transition-colors ${isSelected ? "bg-[var(--primary)] text-white" : "hover:bg-zinc-50"}`}
                    data-testid={`hours-person-${p.id}`}
                  >
                    {p.photo_file_id ? (
                      <AuthImg fileId={p.photo_file_id} className="w-7 h-7 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${isSelected ? "bg-white/20" : "bg-zinc-100"}`}>
                        <Users size={12} className={isSelected ? "text-white" : "text-zinc-500"}/>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{fullName(p)}</div>
                      <div className={`text-[10px] truncate ${isSelected ? "text-white/70" : "text-zinc-500"}`}>{fname}</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => cyclePaymentStatus(p.id, status, e)}
                      onMouseDown={(e) => e.stopPropagation()}
                      className={`w-3 h-3 rounded-full shrink-0 ring-2 transition-transform hover:scale-125 active:scale-95 cursor-pointer ${isSelected ? "ring-white/60" : "ring-white"} ${dotColor} ${(canEdit || isAdmin) ? "hover:ring-zinc-300" : "cursor-default"}`}
                      title={`${dotTitle}\nClick pentru a schimba statusul (unpaid → parțial → plătit)`}
                      data-testid={`pay-bullet-${p.id}`}
                      disabled={!canEdit && !isAdmin}
                      aria-label={dotTitle}
                    />
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT: selected calendar */}
        <div>
          {selected ? (
            <div>
              <div className="card-flat p-4 mb-4 flex items-center gap-4">
                {selected.photo_file_id ? (
                  <AuthImg fileId={selected.photo_file_id} className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-zinc-100 grid place-items-center">
                    <Users size={22} className="text-zinc-400"/>
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-display font-bold text-xl">{fullName(selected)}</div>
                  <div className="text-xs text-zinc-500">
                    {selected.hourly_rate ? `${Number(selected.hourly_rate).toFixed(2)} CHF/h` : "Tarif neconfigurat"}
                    {selected.firma_id && (
                      <span> · {employerCompanies.find((c) => c.id === selected.firma_id)?.name || ""}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/personal/${selected.id}#calendar`)}
                  className="text-xs text-[var(--primary)] hover:underline"
                  data-testid="open-full-profile"
                >
                  Deschide profilul →
                </button>
              </div>
              <CalendarPane personId={selected.id} canEdit={canEdit} defaultRate={defaultRate} />
            </div>
          ) : (
            <div className="card-flat p-12 text-center text-zinc-500 italic">Nicio persoană selectată.</div>
          )}
        </div>
      </div>
      )}
      <div className="mt-8 card-flat p-6">
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
          📱 Cronologie Ore
          <span className="text-xs text-zinc-500 font-normal">— cine a înregistrat / modificat / atașat raport</span>
        </h2>
        <ActivityFeed category="hours" pageSize={8} compact/>
      </div>
    </div>
  );
}
